import {
  InitialEvaluation,
  InitialEvaluationCreateData,
  InitialEvaluationUpdateData,
  InitialEvaluationFilters,
  InitialEvaluationStats,
  PaginatedInitialEvaluations,
  ApiResponse,
} from 'shared/types';
import { ApprovalChainEntry } from 'shared/types';
import api from './apiClient';

// Minimal user shape returned by approval-chain delegate lookups.
export interface AvailableDelegate {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
}

class InitialEvaluationApi {
  // --- CRUD ---

  static async create(data: InitialEvaluationCreateData): Promise<InitialEvaluation> {
    const result = await api.post('/initial-evaluations', data);
    return result.data ?? result;
  }

  static async getAll(filters?: InitialEvaluationFilters): Promise<PaginatedInitialEvaluations> {
    const params: Record<string, unknown> = {};
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params[key] = value;
        }
      });
    }

    const apiResponse: ApiResponse<InitialEvaluation[]> = await api.get('/initial-evaluations', params);
    return {
      data: apiResponse.data || [],
      total: apiResponse.pagination?.total || 0,
      page: apiResponse.pagination?.page || 1,
      limit: apiResponse.pagination?.limit || 20,
      totalPages: apiResponse.pagination?.pages || 1,
    };
  }

  static async getById(id: string): Promise<InitialEvaluation> {
    const result = await api.get(`/initial-evaluations/${id}`);
    return result.data ?? result;
  }

  static async update(id: string, data: InitialEvaluationUpdateData): Promise<InitialEvaluation> {
    const result = await api.put(`/initial-evaluations/${id}`, data);
    return result.data ?? result;
  }

  // --- Workflow ---

  static async submitForApproval(id: string): Promise<InitialEvaluation> {
    const result = await api.post(`/initial-evaluations/${id}/submit`);
    return result.data ?? result;
  }

  static async approve(id: string, data?: { signature?: string; notes?: string }): Promise<InitialEvaluation> {
    const result = await api.post(`/initial-evaluations/${id}/approve`, data || {});
    return result.data ?? result;
  }

  static async reject(id: string, data: { reason: string; category: string }): Promise<InitialEvaluation> {
    const result = await api.post(`/initial-evaluations/${id}/reject`, data);
    return result.data ?? result;
  }

  static async partialApproval(id: string, data: { notes: string }): Promise<InitialEvaluation> {
    const result = await api.post(`/initial-evaluations/${id}/partial`, data);
    return result.data ?? result;
  }

  static async resolvePartial(id: string, data?: { notes?: string }): Promise<InitialEvaluation> {
    const result = await api.post(`/initial-evaluations/${id}/resolve-partial`, data || {});
    return result.data ?? result;
  }

  static async delegate(id: string, data: { toApproverId: string; reason?: string }): Promise<InitialEvaluation> {
    const result = await api.post(`/initial-evaluations/${id}/delegate`, data);
    return result.data ?? result;
  }

  static async close(id: string): Promise<InitialEvaluation> {
    const result = await api.post(`/initial-evaluations/${id}/close`);
    return result.data ?? result;
  }

  // --- Stats ---

  static async getStats(): Promise<InitialEvaluationStats> {
    const result = await api.get('/initial-evaluations/stats');
    return result.data ?? result;
  }

  // --- Approval Chain Management ---

  static async getApprovalChain(id: string): Promise<{ success: boolean; data: ApprovalChainEntry[] }> {
    return api.get(`/initial-evaluations/${id}/approval-chain`);
  }

  static async addToChain(
    id: string,
    data: { approverId: string; isRequired: boolean; isBench: boolean }
  ): Promise<{ success: boolean; data: InitialEvaluation }> {
    return api.post(`/initial-evaluations/${id}/approval-chain/add`, data);
  }

  static async removeFromChain(
    id: string,
    data?: { reason?: string }
  ): Promise<{ success: boolean; data: InitialEvaluation }> {
    return api.post(`/initial-evaluations/${id}/approval-chain/remove`, data || {});
  }

  static async getAvailableDelegates(
    id: string,
    search?: string
  ): Promise<{ success: boolean; data: AvailableDelegate[]; total: number }> {
    const params = search ? { search } : undefined;
    return api.get(`/initial-evaluations/${id}/approval-chain/available-delegates`, params);
  }

  // --- Resolution Documents ---

  static async getResolutionUploadUrl(
    id: string,
    contentType: string
  ): Promise<{ success: boolean; data: { presignedPost: { url: string; fields: Record<string, string> }; key: string } }> {
    return api.post(`/initial-evaluations/${id}/resolution-upload-url`, { contentType });
  }
}

export default InitialEvaluationApi;
